import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import LogoUpload from '@/components/LogoUpload';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';
import { useTabs } from '@/context/TabsContext';

export default function CreateHotel({ user, isTab, tabId }) {
  const navigate = useNavigate();
  const { closeTab } = useTabs();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [useExistingAdmin, setUseExistingAdmin] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_id: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pin_code: '',
      country: 'India'
    },
    logo_url: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    existing_admin_id: ''
  });
  
  const handleClose = () => {
    if (isTab && tabId) {
      closeTab(tabId);
    } else {
      navigate('/admin/super-admin/dashboard');
    }
  };

  useEffect(() => {
    loadCompanies();
    loadAdminUsers();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      
      // Auto-select company for company_admin
      if (user?.role === 'company_admin') {
        const userCompanyId = user.company_id || (user.company_ids && user.company_ids[0]);
        if (userCompanyId) {
          setFormData(prev => ({ ...prev, company_id: userCompanyId }));
        }
      }
    } catch (error) {
      console.error('Failed to load companies');
    }
  };


  const loadAdminUsers = async () => {
    try {
      const response = await api.get('/super-admin/users/admins');
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Failed to load admin users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id) {
      toast.error('Please select a company for this hotel');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company_id: formData.company_id,
        // Flatten address for backward compatibility with backend
        address: formData.address.address_line1 + (formData.address.address_line2 ? ', ' + formData.address.address_line2 : ''),
        city: formData.address.city,
        state: formData.address.state,
        country: formData.address.country,
        pin_code: formData.address.pin_code,
        logo_url: formData.logo_url || null,
        // Also send structured address
        address_details: formData.address
      };

      if (useExistingAdmin && formData.existing_admin_id) {
        payload.existing_admin_id = formData.existing_admin_id;
      } else if (!useExistingAdmin && formData.admin_name && formData.admin_email && formData.admin_password) {
        payload.admin_name = formData.admin_name;
        payload.admin_email = formData.admin_email;
        payload.admin_password = formData.admin_password;
      }

      const response = await api.post('/super-admin/hotels', payload);
      toast.success('Hotel created successfully!');
      if (response.data.admin_email) {
        toast.info(`Admin Email: ${response.data.admin_email}`);
      }
      // Close tab or navigate back
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create hotel'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    handleClose();
  };

  return (
    <div className="space-y-6" data-testid="create-hotel-page">
      <div>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Hotels
        </Button>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Add New Hotel
        </h1>
        <p className="text-muted-foreground mt-1">Onboard a new hotel to the platform</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hotel Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Hotel Information
              </CardTitle>
              <CardDescription>Basic details about the hotel</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Hotel Logo</Label>
                <LogoUpload
                  currentLogo={formData.logo_url}
                  onLogoChange={(url) => setFormData({...formData, logo_url: url})}
                  entityType="hotel"
                  size="lg"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Hotel Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="hotel-name-input"
                />
              </div>
              
              {/* Company Selection */}
              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <Select 
                  value={formData.company_id || "none"} 
                  onValueChange={(value) => setFormData({...formData, company_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="company-select">
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select a company</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        <div className="flex flex-col">
                          <span>{company.name}</span>
                          {company.legal_name && (
                            <span className="text-xs text-muted-foreground">{company.legal_name}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {companies.length === 0 && (
                  <p className="text-xs text-amber-600">
                    No companies available. Please create a company first.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Hotel Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="hotel-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(value) => setFormData({...formData, phone: value})}
                  required
                  data-testid="hotel-phone-input"
                />
              </div>
              
              {/* Address Section */}
              <div className="space-y-2">
                <Label>Address *</Label>
                <AddressInput
                  value={formData.address}
                  onChange={(value) => setFormData({...formData, address: value})}
                  required
                  data-testid="hotel-address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Admin User */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Hotel Admin (Optional)
              </CardTitle>
              <CardDescription>Assign an admin to manage this hotel</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Toggle between existing and new admin */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Use Existing Admin</p>
                  <p className="text-sm text-muted-foreground">
                    Assign an existing hotel admin user
                  </p>
                </div>
                <Switch
                  checked={useExistingAdmin}
                  onCheckedChange={setUseExistingAdmin}
                  data-testid="use-existing-admin-toggle"
                />
              </div>

              {useExistingAdmin ? (
                /* Existing Admin Selection */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="existing_admin">Select Admin User</Label>
                    <Select 
                      value={formData.existing_admin_id || "none"} 
                      onValueChange={(value) => setFormData({...formData, existing_admin_id: value === "none" ? "" : value})}
                    >
                      <SelectTrigger data-testid="existing-admin-select">
                        <SelectValue placeholder="Select an admin user (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Admin (Assign Later)</SelectItem>
                        {adminUsers.map(admin => (
                          <SelectItem key={admin.id} value={admin.id}>
                            <div className="flex flex-col">
                              <span>{admin.name}</span>
                              <span className="text-xs text-muted-foreground">{admin.email}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      One admin can manage multiple hotels
                    </p>
                  </div>

                  {adminUsers.length === 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-800">
                        No admin users available. Create a new admin below or add admins from User Management.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Create New Admin */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_name">Admin Name</Label>
                    <Input
                      id="admin_name"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                      placeholder="Leave empty to skip admin creation"
                      data-testid="admin-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin_email">Admin Email</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                      data-testid="admin-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin_password">Admin Password</Label>
                    <Input
                      id="admin_password"
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                      minLength={6}
                      data-testid="admin-password-input"
                    />
                    <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                  </div>
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">Note:</p>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Hotel will be created with TRIAL status</li>
                  <li>Admin creation is optional - you can assign later</li>
                  <li>One admin can manage multiple hotels</li>
                  <li>Admin will have full access to manage their assigned hotels</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/super-admin/dashboard')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-hotel-button"
          >
            {loading ? 'Creating...' : 'Create Hotel'}
          </Button>
        </div>
      </form>
    </div>
  );
}
